import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Link,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Facebook as FacebookIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { authService } from '../services/authService';
import { exposureService } from '../services/exposureService';
import { dataService } from '../services/dataService';
import { formatCommentExportTime } from '../utils/formatFacebookActivityLine';
import FacebookActivityLine from '../components/FacebookActivityLine';
import LogoutActions from '../components/LogoutActions';
import DashboardRiskHero from '../components/dashboard/DashboardRiskHero';
import { resolveActivePlatform } from '../utils/resolveActivePlatform';
import { tokens } from '../theme/tokens';

const DASHBOARD_CARD_BORDER = `1px solid ${tokens.border.strong}`;
const DASHBOARD_PAPER_SURFACE = {
  bgcolor: tokens.bg.card,
  border: DASHBOARD_CARD_BORDER,
  boxShadow: '0 8px 28px rgba(2, 6, 23, 0.45)',
  borderRadius: `${tokens.radius.card}px`,
};

const STAT_CARD_SX = {
  height: '100%',
  bgcolor: tokens.bg.card,
  border: `1px solid ${tokens.border.subtle}`,
  borderRadius: `${tokens.radius.card}px`,
  cursor: 'pointer',
  boxShadow: '0 6px 20px rgba(2, 6, 23, 0.35)',
  transition: 'box-shadow 0.22s ease, transform 0.22s ease, border-color 0.22s ease',
  '&:hover': {
    boxShadow: '0 14px 36px rgba(2, 6, 23, 0.5)',
    transform: 'translateY(-3px)',
    borderColor: tokens.border.strong,
  },
  '&:focus-visible': {
    outline: `2px solid ${tokens.accent.cyan}`,
    outlineOffset: 2,
  },
};

const STAT_ICON_BOX_SX = {
  width: 46,
  height: 46,
  borderRadius: `${tokens.radius.control}px`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'rgba(148, 163, 184, 0.08)',
  border: `1px solid ${tokens.border.subtle}`,
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.05)',
};

const sectionMb = tokens.layout.sectionMb;

const SECTION_TITLE_SX = {
  fontWeight: 700,
  letterSpacing: '-0.02em',
  mb: 2,
  color: 'text.primary',
};

function activePlatformLabel(user) {
  if (!user) return '';
  const p = resolveActivePlatform(user);
  if (p === 'youtube') return 'YouTube';
  if (p === 'facebook') return 'Facebook';
  return 'Reddit';
}

function dashboardAlertStyles(theme, severity) {
  const key =
    severity === 'error'
      ? 'error'
      : severity === 'success'
        ? 'success'
        : severity === 'warning'
          ? 'warning'
          : 'info';
  const c = theme.palette[key];
  return {
    mb: sectionMb,
    borderRadius: `${tokens.radius.card}px`,
    bgcolor: alpha(c.main, 0.07),
    borderColor: alpha(c.main, 0.45),
    color: tokens.text.primary,
    alignItems: 'flex-start',
    '& .MuiAlert-icon': { color: c.light },
    '& .MuiAlert-message': { color: tokens.text.primary },
  };
}

/**
 * Stat cards must match `/privacy/risk_score/` raw detection counts — the findings list merges
 * multiple matches on one comment into a single row, which under-counts totals.
 */
function mergeDashboardExposureStats(exposureStats, risk) {
  if (!risk || typeof risk.total_unresolved !== 'number') {
    return exposureStats;
  }
  const br = exposureStats.byRiskLevel || {};
  const resolved = typeof risk.resolved === 'number' ? risk.resolved : exposureStats.resolved;
  const unresolved = risk.total_unresolved;
  return {
    ...exposureStats,
    total: unresolved + resolved,
    unresolved,
    resolved,
    byRiskLevel: {
      high: typeof risk.high === 'number' ? risk.high : br.high,
      medium: typeof risk.medium === 'number' ? risk.medium : br.medium,
      low: typeof risk.low === 'number' ? risk.low : br.low,
    },
  };
}

/**
 * Dashboard Page
 * Main dashboard showing user info, scan status, and exposure summary
 */
const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [stats, setStats] = useState(null);
  const [riskScore, setRiskScore] = useState(null);
  const [error, setError] = useState(null);
  const [portabilityMessage, setPortabilityMessage] = useState(null);
  const [takeoutUploading, setTakeoutUploading] = useState(false);
  const [takeoutMessage, setTakeoutMessage] = useState(null);
  const [recentYouTubeExposures, setRecentYouTubeExposures] = useState([]);
  const [recentFacebookExposures, setRecentFacebookExposures] = useState([]);
  const [fbDyiUploading, setFbDyiUploading] = useState(false);
  const [fbDyiMessage, setFbDyiMessage] = useState(null);
  const takeoutInputRef = useRef(null);
  const fbDyiInputRef = useRef(null);

  // Handle YouTube Data Portability callback query params
  useEffect(() => {
    const youtubePortability = searchParams.get('youtube_portability');
    const err = searchParams.get('error');
    if (youtubePortability === 'success') {
      setPortabilityMessage({ type: 'success', text: 'YouTube comment export permission granted. You can now run a scan to include comments on other channels\' videos.' });
      setSearchParams({}, { replace: true });
    } else if (err && err.startsWith('youtube_portability_')) {
      const messages = {
        login_required_for_export: 'Please log in first, then try "Export my YouTube comments" again.',
        youtube_portability_denied: 'YouTube comment export was not granted.',
        youtube_portability_missing_code: 'Authorization was interrupted. Please try again.',
        youtube_portability_session_expired: 'Session expired. Please log in again and try "Export my YouTube comments".',
        youtube_portability_token_failed: 'Could not save export permission. Please try again.',
        youtube_portability_auth_failed: 'Export permission failed. Please try again.',
      };
      setPortabilityMessage({ type: 'error', text: messages[err] || 'Something went wrong. Please try again.' });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    loadDashboardData();
    
    // Listen for exposure resolution events to auto-refresh dashboard
    const handleExposureResolved = (event) => {
      console.log('Exposure resolved event received:', event.detail);
      console.log('Refreshing dashboard data...');
      // Force a full refresh of dashboard data
      loadDashboardData();
    };
    
    window.addEventListener('exposureResolved', handleExposureResolved);
    
    // Refresh on focus after a short delay so file input change event runs first
    const handleFocus = () => {
      setTimeout(() => loadDashboardData(true), 150);
    };
    window.addEventListener('focus', handleFocus);
    
    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('exposureResolved', handleExposureResolved);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loadDashboardData = async (backgroundRefresh = false) => {
    try {
      if (!backgroundRefresh) {
        setLoading(true);
        setError(null);
      }

      // Get current user
      const userData = await authService.getCurrentUser();
      setUser(userData);

      // Get username from user data (display name; must match backend User lookup incl. facebook_name)
      const username =
        userData?.username ||
        userData?.reddit_username ||
        userData?.youtube_channel_title ||
        userData?.facebook_name;
      if (!username) {
        setError('Username not found. Please log in again.');
        if (!backgroundRefresh) setLoading(false);
        return;
      }

      const activePlatform = resolveActivePlatform(userData);
      const userId = userData?.id;
      const privacyParams = { platform: activePlatform, ...(userId ? { user_id: userId } : {}) };

      // Sync privacy_findings from MongoDB (posts/comments) without calling Graph — keeps
      // dashboard/exposures aligned with stored scans when Graph token fails or user only used DYI.
      if (activePlatform === 'facebook') {
        try {
          await dataService.reanalyzeFacebookFromStored(username);
        } catch (e) {
          console.warn('Facebook re-analyze from DB:', e?.response?.data || e?.message);
        }
      }

      // Get exposure stats filtered to the active platform (user_id avoids username/display mismatches)
      const statsData = await exposureService.getExposureStats(username, privacyParams);

      // Pull top unresolved exposures for dashboard quick actions (YouTube / Facebook)
      const youtubeExposures = activePlatform === 'youtube'
        ? await exposureService.getExposures(username, privacyParams)
        : [];
      const topYouTube = (youtubeExposures || [])
        .filter((e) => {
          if (e?.platform !== 'youtube' || e?.is_resolved) return false;
          const looksLikeComment = e?.source === 'comment' || !!e?.comment_id;
          return looksLikeComment;
        })
        .map((e) => {
          // Backward compatibility for old findings that might miss `url`.
          const fallbackUrl = e?.post_id ? `https://www.youtube.com/watch?v=${e.post_id}` : '';
          return {
            ...e,
            url: e?.url || fallbackUrl,
          };
        })
        .sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0))
        .slice(0, 5);
      setRecentYouTubeExposures(topYouTube);

      const facebookExposures = activePlatform === 'facebook'
        ? await exposureService.getExposures(username, privacyParams)
        : [];
      // Show post, comment, and profile-derived Facebook exposures (not comments-only).
      const topFacebook = (facebookExposures || [])
        .filter((e) => {
          if (e?.platform !== 'facebook' || e?.is_resolved) return false;
          return true;
        })
        .sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0))
        .slice(0, 5);
      setRecentFacebookExposures(topFacebook);

      const riskData = await exposureService.getRiskScore(username, privacyParams);
      setStats(mergeDashboardExposureStats(statsData, riskData));
      setRiskScore(riskData);

      const fbGraphSyncKey = `safesocial_fb_graph_session_sync_v1:${userData?.id || username || ''}`;
      if (
        activePlatform === 'facebook' &&
        !backgroundRefresh &&
        username &&
        !sessionStorage.getItem(fbGraphSyncKey)
      ) {
        void dataService.scanFacebook(username).then(
          (res) => {
            if (res?.status === 'success') {
              sessionStorage.setItem(fbGraphSyncKey, '1');
              return loadDashboardData(true);
            }
            return undefined;
          },
          (e) => console.warn('Background Facebook Graph sync skipped:', e?.message || e),
        );
      }

      if (!backgroundRefresh) setLoading(false);
    } catch (error) {
      console.error('Load dashboard error:', error);
      setError('Failed to load dashboard data');
      if (!backgroundRefresh) setLoading(false);
      
      // If unauthorized, redirect to login
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Redirect anyway
      navigate('/login');
    }
  };

  const handleTakeoutUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setTakeoutMessage({ type: 'error', text: 'Please select a ZIP file from Google Takeout.' });
      return;
    }
    setTakeoutUploading(true);
    setTakeoutMessage(null);
    try {
      const result = await dataService.uploadYouTubeTakeout(file);
      if (result?.status === 'success') {
        const data = result?.data || {};
        setTakeoutMessage({
          type: 'success',
          text: `Processed ${data.comments_parsed || 0} comments. Found ${data.exposures_found || 0} privacy exposure(s).`,
        });
        await loadDashboardData();
      } else {
        setTakeoutMessage({ type: 'error', text: result?.message || 'Upload failed.' });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Upload failed.';
      setTakeoutMessage({ type: 'error', text: msg });
    } finally {
      setTakeoutUploading(false);
      if (takeoutInputRef.current) takeoutInputRef.current.value = '';
    }
  };

  const handleFacebookDyiUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.zip') && !lower.endsWith('.json')) {
      setFbDyiMessage({
        type: 'error',
        text: 'Please choose the ZIP (or a single JSON) from Facebook → Download your information.',
      });
      return;
    }
    const username =
      user?.username || user?.reddit_username || user?.youtube_channel_title || user?.facebook_name;
    if (!username) {
      setFbDyiMessage({ type: 'error', text: 'Could not determine your account. Please log in again.' });
      return;
    }
    setFbDyiUploading(true);
    setFbDyiMessage(null);
    try {
      const result = await dataService.uploadFacebookFile(file, 'posts', username);
      if (result?.status === 'success') {
        const d = result?.data || {};
        setFbDyiMessage({
          type: 'success',
          text:
            result?.message ||
            `Processed ${d.posts_parsed ?? ''} posts / ${d.comments_parsed ?? ''} comments. Found ${d.exposures_found ?? 0} exposure(s).`,
        });
        await loadDashboardData();
      } else {
        setFbDyiMessage({ type: 'error', text: result?.message || 'Upload failed.' });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Upload failed.';
      setFbDyiMessage({ type: 'error', text: msg });
    } finally {
      setFbDyiUploading(false);
      if (fbDyiInputRef.current) fbDyiInputRef.current.value = '';
    }
  };

  const handleScan = async () => {
    try {
      setScanning(true);
      setError(null);

      const username =
        user?.username || user?.reddit_username || user?.youtube_channel_title || user?.facebook_name;
      if (!username) {
        setError('Username not found. Please log in again.');
        setScanning(false);
        return;
      }

      if (user?.active_platform === 'facebook') {
        try {
          const res = await dataService.scanFacebook(username);
          if (res.status === 'success') {
            const hint = res.hint ? ` ${res.hint}` : '';
            alert((res.message || 'Scan completed.') + hint);
            await loadDashboardData();
          } else {
            setError(res.message || 'Scan failed.');
          }
        } catch (e) {
          const d = e.response?.data;
          setError(d?.message || d?.hint || e.message || 'Scan failed.');
        } finally {
          setScanning(false);
        }
        return;
      }

      const platform = user?.active_platform === 'youtube' ? 'youtube' : 'reddit';
      const scanUsername =
        platform === 'youtube' ? username : user?.reddit_username || username;

      const result = await dataService.scanAndAnalyze(scanUsername, {
        platform,
        limit_posts: 100,
        limit_comments: 100
      });
      
      console.log('Scan and analyze result:', result);
      
      // Show success message
      if (result.status === 'success') {
        const fetched = result.data?.fetched || {};
        const analysis = result.data?.analysis || {};
        
        alert(
          `Scan complete!\n\n` +
          `Fetched: ${fetched.new_posts || 0} new posts, ${fetched.new_comments || 0} new comments\n` +
          `Found: ${analysis.exposures_found || 0} privacy exposures`
        );
        
        // Refresh dashboard data
        await loadDashboardData();
      } else {
        setError(result.message || 'Scan failed');
      }
      
      setScanning(false);
    } catch (error) {
      console.error('Scan error:', error);
      setError('Failed to scan. Please try again.');
      setScanning(false);
    }
  };

  const handleRiskRefresh = async () => {
    const userData = await authService.getCurrentUser();
    const redditUser = userData?.reddit_username;
    if (redditUser) {
      try {
        setScanning(true);
        await dataService.scanAndAnalyze(redditUser, {
          platform: 'reddit',
          limit_posts: 100,
          limit_comments: 100,
        });
      } catch (e) {
        console.error('Reddit sync on refresh failed:', e);
      } finally {
        setScanning(false);
      }
    }
    await loadDashboardData();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: tokens.layout.containerPy, textAlign: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            py: 8,
            px: 3,
            ...DASHBOARD_PAPER_SURFACE,
          }}
        >
          <CircularProgress size={56} thickness={4} sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary', fontWeight: 600, fontSize: '1.125rem' }}>
            Loading dashboard...
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: tokens.layout.containerPy, pb: tokens.layout.containerPb }}>
      <Paper
        elevation={0}
        sx={{
          mb: sectionMb,
          border: tokens.hero.border,
          borderRadius: `${tokens.radius.card}px`,
          boxShadow: `${tokens.hero.shadow}, inset 0 1px 0 rgba(255, 255, 255, 0.06)`,
          background: tokens.hero.gradient,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 2.5, md: 2 },
            p: { xs: 2.5, sm: 3.25 },
            pl: { xs: 2, sm: 2.75 },
            borderLeft: `3px solid ${tokens.accent.cyan}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {user && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  color: tokens.text.secondary,
                  mb: 1,
                  fontSize: '0.72rem',
                }}
              >
                {activePlatformLabel(user)} · Overview
              </Typography>
            )}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.65rem', sm: '2rem' },
                color: tokens.text.primary,
                mb: user ? 1 : 0,
                lineHeight: 1.2,
              }}
            >
              Dashboard
            </Typography>
            {user && (
              <Typography variant="body1" sx={{ maxWidth: 560, fontSize: '1.0625rem', lineHeight: 1.6, color: tokens.text.secondary }}>
                Welcome back, {user.username || user.reddit_username || user.youtube_channel_title || 'User'}!
              </Typography>
            )}
          </Box>
          <Box sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', md: 'center' } }}>
            <LogoutActions variant="dashboard" />
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" variant="outlined" sx={(theme) => dashboardAlertStyles(theme, 'error')}>
          {error}
        </Alert>
      )}

      {portabilityMessage && (
        <Alert
          severity={portabilityMessage.type}
          variant="outlined"
          sx={(theme) => dashboardAlertStyles(theme, portabilityMessage.type)}
          onClose={() => setPortabilityMessage(null)}
        >
          {portabilityMessage.text}
        </Alert>
      )}

      {takeoutMessage && (
        <Alert
          severity={takeoutMessage.type}
          variant="outlined"
          sx={(theme) => dashboardAlertStyles(theme, takeoutMessage.type)}
          onClose={() => setTakeoutMessage(null)}
        >
          {takeoutMessage.text}
        </Alert>
      )}

      {fbDyiMessage && (
        <Alert
          severity={fbDyiMessage.type}
          variant="outlined"
          sx={(theme) => dashboardAlertStyles(theme, fbDyiMessage.type)}
          onClose={() => setFbDyiMessage(null)}
        >
          {fbDyiMessage.text}
        </Alert>
      )}

      {user?.active_platform === 'facebook' && (
        <Alert severity="info" variant="outlined" sx={(theme) => dashboardAlertStyles(theme, 'info')}>
          Upload your Facebook <strong>Download Your Information</strong> ZIP below. After it finishes processing, open{' '}
          <strong>Show exposures</strong> to review comment/post PII and ad data in one place.
        </Alert>
      )}

      {/* Risk Score Card */}
      {riskScore && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            mb: sectionMb,
            ...DASHBOARD_PAPER_SURFACE,
            borderTop: `1px solid ${tokens.border.subtle}`,
          }}
        >
          <DashboardRiskHero
            score={riskScore.risk_score ?? 0}
            level={riskScore.level || riskScore.risk_level}
            onRefresh={handleRiskRefresh}
            refreshing={scanning}
            disabled={scanning}
          />
        </Paper>
      )}

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: sectionMb }}>
        <Grid item xs={12} sm={4}>
          <Card
            role="button"
            tabIndex={0}
            onClick={() => navigate('/exposures')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/exposures');
              }
            }}
            sx={{ ...STAT_CARD_SX, borderTop: '3px solid', borderTopColor: 'warning.main' }}
          >
            <CardContent sx={{ py: 2.75, px: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      ...STAT_ICON_BOX_SX,
                      color: 'warning.dark',
                      '& svg': { color: 'warning.main' },
                    }}
                  >
                    <WarningIcon fontSize="large" />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                      {stats?.total || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      Total Exposures
                    </Typography>
                  </Box>
                </Stack>
                <ChevronRightIcon sx={{ color: 'text.secondary', opacity: 0.65, flexShrink: 0 }} aria-hidden />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            role="button"
            tabIndex={0}
            onClick={() => navigate('/exposures?filterResolved=resolved')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/exposures?filterResolved=resolved');
              }
            }}
            sx={{ ...STAT_CARD_SX, borderTop: '3px solid', borderTopColor: 'success.main' }}
          >
            <CardContent sx={{ py: 2.75, px: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      ...STAT_ICON_BOX_SX,
                      color: 'success.dark',
                      '& svg': { color: 'success.main' },
                    }}
                  >
                    <CheckCircleIcon fontSize="large" />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                      {stats?.resolved || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      Resolved
                    </Typography>
                  </Box>
                </Stack>
                <ChevronRightIcon sx={{ color: 'text.secondary', opacity: 0.65, flexShrink: 0 }} aria-hidden />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            role="button"
            tabIndex={0}
            onClick={() => navigate('/exposures?filterResolved=open')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/exposures?filterResolved=open');
              }
            }}
            sx={{ ...STAT_CARD_SX, borderTop: '3px solid', borderTopColor: 'error.main' }}
          >
            <CardContent sx={{ py: 2.75, px: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      ...STAT_ICON_BOX_SX,
                      color: 'error.dark',
                      '& svg': { color: 'error.main' },
                    }}
                  >
                    <WarningIcon fontSize="large" />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                      {stats?.unresolved || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      Needs Attention
                    </Typography>
                  </Box>
                </Stack>
                <ChevronRightIcon sx={{ color: 'text.secondary', opacity: 0.65, flexShrink: 0 }} aria-hidden />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Exposure by Risk Level */}
      {riskScore && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: sectionMb,
            ...DASHBOARD_PAPER_SURFACE,
          }}
        >
          <Typography variant="h5" sx={SECTION_TITLE_SX}>
            Exposures by Risk Level
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="error.main" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>
                  {stats?.byRiskLevel?.high || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5 }}>
                  High Risk
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>
                  {stats?.byRiskLevel?.medium || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5 }}>
                  Medium Risk
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>
                  {stats?.byRiskLevel?.low || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5 }}>
                  Low Risk
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* YouTube quick links to exposed comments/videos */}
      {user?.active_platform === 'youtube' && recentYouTubeExposures.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: sectionMb,
            ...DASHBOARD_PAPER_SURFACE,
          }}
        >
          <Typography variant="h5" sx={SECTION_TITLE_SX}>
            Exposed YouTube Comments (Quick Links)
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: '1.0625rem' }}>
            Open the exact video/comment and remove or edit the exposed data on YouTube.
          </Typography>
          <Stack spacing={1.25}>
            {recentYouTubeExposures.map((exp) => {
              const channelUrl = exp?.channel_url && exp.channel_url.startsWith('http')
                ? exp.channel_url
                : '';
              return (
              <Box
                key={exp._id}
                sx={{
                  p: 2.25,
                  border: DASHBOARD_CARD_BORDER,
                  borderRadius: 2,
                  backgroundColor: tokens.bg.cardElevated,
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': { boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)' },
                }}
              >
                <Typography variant="body1" sx={{ mb: 0.5, fontSize: '1.0625rem', color: tokens.text.primary }}>
                  <strong>Video:</strong> {exp.video_title || (exp.url ? 'Open video' : 'Not available in this export')}
                </Typography>
                {exp.channel_title && (
                  <Typography variant="body1" sx={{ mb: 0.5, fontSize: '1.0625rem', color: tokens.text.primary }}>
                    <strong>Channel:</strong>{' '}
                    {channelUrl ? (
                      <Link href={channelUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600 }}>
                        {exp.channel_title}
                      </Link>
                    ) : (
                      <Box component="span" sx={{ fontWeight: 600 }}>{exp.channel_title}</Box>
                    )}
                  </Typography>
                )}
                {exp.created_at && (
                  <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 600, color: tokens.text.primary }}>
                    Comment time: {formatCommentExportTime(exp.created_at)}
                  </Typography>
                )}
                <Typography variant="body1" color="text.secondary" sx={{ mb: 0.75, fontSize: '1rem' }}>
                  {exp.exposure_type === 'address_hint' || exp.exposure_type === 'location'
                    ? 'Address/location detected in this comment.'
                    : `${(exp.exposure_type || 'exposure').replace('_', ' ')} detected in this comment.`}
                </Typography>
                {exp.url ? (
                  <Link
                    href={exp.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ fontWeight: 700, fontSize: '1.0625rem' }}
                  >
                    Open on YouTube and edit/delete this comment →
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9375rem' }}>
                    Direct target video/channel link was not present in this Takeout record, so exact comment location cannot be opened for this item.
                  </Typography>
                )}
              </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Facebook DYI: quick links to post/activity (same data as Exposures API) */}
      {resolveActivePlatform(user) === 'facebook' && recentFacebookExposures.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: sectionMb,
            ...DASHBOARD_PAPER_SURFACE,
          }}
        >
          <Typography variant="h5" sx={SECTION_TITLE_SX}>
            Exposed Facebook comments (quick links)
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: '1.0625rem' }}>
            Open the post when a URL was found in your export, or use Activity to find the comment when no direct link exists.
          </Typography>
          <Stack spacing={1.25}>
            {recentFacebookExposures.map((exp) => {
              const activityHref = exp.url || exp.facebook_activity_fallback_url || '';
              return (
              <Box
                key={exp._id}
                sx={{
                  p: 2.25,
                  border: DASHBOARD_CARD_BORDER,
                  borderRadius: 2,
                  backgroundColor: tokens.bg.cardElevated,
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': { boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)' },
                }}
              >
                {exp.context_title ? (
                  activityHref ? (
                    <FacebookActivityLine text={exp.context_title} href={activityHref} />
                  ) : (
                    <FacebookActivityLine text={exp.context_title} />
                  )
                ) : null}
                {exp.created_at && (
                  <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 700, color: tokens.text.primary, fontSize: '1.0625rem' }}>
                    Comment time (from your export): {formatCommentExportTime(exp.created_at)}
                  </Typography>
                )}
                <Typography variant="body1" color="text.secondary" sx={{ mb: 0.75, fontSize: '1rem' }}>
                  {(exp.exposure_type || 'exposure').replace('_', ' ')} detected in this comment.
                </Typography>
                {exp.url ? (
                  <Link
                    href={exp.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ fontWeight: 700, fontSize: '1.0625rem' }}
                  >
                    Open on Facebook (post or comment context) →
                  </Link>
                ) : exp.facebook_activity_fallback_url ? (
                  <Box>
                    <Link
                      href={exp.facebook_activity_fallback_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ fontWeight: 700, fontSize: '1.0625rem' }}
                    >
                      Open your Activity log →
                    </Link>
                    <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.75, fontSize: '0.9375rem' }}>
                      Exact comment link was not in your export — find this interaction under Your activity.
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9375rem' }}>
                    No link available. Re-upload your Download Your Information ZIP from the dashboard.
                  </Typography>
                )}
              </Box>
            );
            })}
          </Stack>
        </Paper>
      )}

      {/* Action Buttons */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: sectionMb,
          ...DASHBOARD_PAPER_SURFACE,
        }}
      >
        <Typography variant="h5" sx={SECTION_TITLE_SX}>
          Quick Actions
        </Typography>
        <Stack direction="row" spacing={2} rowGap={2} columnGap={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            size="large"
            onClick={handleScan}
            disabled={scanning}
            startIcon={scanning ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {scanning ? 'Scanning...' : 'Scan for New Data'}
          </Button>

          {user?.active_platform === 'youtube' && (
            <>
              <Button
                variant="outlined"
                size="large"
                onClick={() => authService.startYouTubeCommentsExport()}
              >
                Export my YouTube comments
              </Button>
              <Button
                variant="outlined"
                size="large"
                component="label"
                disabled={takeoutUploading}
                startIcon={takeoutUploading ? <CircularProgress size={20} /> : null}
              >
                {takeoutUploading ? 'Uploading...' : 'Upload YouTube Takeout'}
                <input
                  ref={takeoutInputRef}
                  type="file"
                  accept=".zip"
                  hidden
                  onChange={handleTakeoutUpload}
                />
              </Button>
            </>
          )}

          {user?.active_platform === 'facebook' && (
            <Button
              variant="outlined"
              size="large"
              component="label"
              disabled={fbDyiUploading}
              startIcon={fbDyiUploading ? <CircularProgress size={20} /> : <FacebookIcon />}
            >
              {fbDyiUploading ? 'Uploading…' : 'Upload Facebook DYI (ZIP or JSON)'}
              <input
                ref={fbDyiInputRef}
                type="file"
                accept=".zip,.json,application/json"
                hidden
                onChange={handleFacebookDyiUpload}
              />
            </Button>
          )}

          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/exposures')}
          >
            Show exposures
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/recommendations')}
          >
            View Recommendations
          </Button>
        </Stack>

      </Paper>

      {/* Connected Platforms */}
      {user && user.platforms && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            ...DASHBOARD_PAPER_SURFACE,
          }}
        >
          <Typography variant="h5" sx={SECTION_TITLE_SX}>
            Connected Platforms
          </Typography>
          <Stack direction="row" spacing={1}>
            {user.platforms.map((platform) => (
              <Chip
                key={platform}
                label={platform.charAt(0).toUpperCase() + platform.slice(1)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>
        </Paper>
      )}
    </Container>
  );
};

export default DashboardPage;
